import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Layers,
  Users,
  Loader2,
  CheckCircle2,
  Trash2,
  RefreshCw,
  CircleDot,
  Filter,
  ShieldCheck,
  AlertCircle,
  Clock,
  Send,
} from "lucide-react";
import { generateInvoicePDF } from "@/utils/pdfGenerator";

const APPROVAL_STATUS_CONFIG = {
  pending: { label: "Pending Review", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  confirmed: { label: "Translator Confirmed", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  disputed: { label: "Disputed", bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  approved: { label: "Admin Approved", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  generated: { label: "Generated", bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" },
  sent: { label: "Sent", bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
  paid: { label: "Paid", bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500" },
  unpaid: { label: "Unpaid", bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  overdue: { label: "Overdue", bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" },
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.generated;
  return (
    <Badge className={`${config.bg} ${config.text} border-0 gap-1.5 capitalize`} data-testid={`status-badge-${status}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </Badge>
  );
};

const ExportCenter = () => {
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [statusDialog, setStatusDialog] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", payment_date: "", notes: "" });
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [approving, setApproving] = useState(null);
  const [approvalFilter, setApprovalFilter] = useState("all");
  const [filters, setFilters] = useState({
    taskType: "",
    resource: "",
    status: "Completed",
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/invoices`),
      ]);
      setStats(statsRes.data);
      setInvoices(invoicesRes.data.invoices || []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const statusCounts = invoices.reduce((acc, inv) => {
    const s = inv.status || "generated";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const handleStatusUpdate = async () => {
    if (!statusDialog || !statusForm.status) return;
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/invoices/${statusDialog.id}/status`, statusForm);
      toast.success(`Invoice marked as ${statusForm.status}`);
      setStatusDialog(null);
      setStatusForm({ status: "", payment_date: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openStatusDialog = (invoice) => {
    setStatusDialog(invoice);
    setStatusForm({
      status: invoice.status || "generated",
      payment_date: invoice.payment_date || "",
      notes: invoice.status_notes || "",
    });
  };

  const handleApproveInvoice = async (invoiceId) => {
    setApproving(invoiceId);
    try {
      await axios.post(`${API}/admin/approve/${invoiceId}`);
      toast.success("Invoice approved successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve invoice");
    } finally {
      setApproving(null);
    }
  };

  const getApprovalStatus = (invoice) => {
    if (invoice.admin_approved) return "approved";
    if (invoice.translator_disputed) return "disputed";
    if (invoice.translator_confirmed) return "confirmed";
    return "pending";
  };

  // Filter by approval status
  const getFilteredInvoices = () => {
    let result = statusFilter === "all" ? invoices : invoices.filter(inv => inv.status === statusFilter);
    
    if (approvalFilter === "pending_approval") {
      result = result.filter(inv => inv.translator_confirmed && !inv.admin_approved);
    } else if (approvalFilter === "awaiting_confirmation") {
      result = result.filter(inv => !inv.translator_confirmed && !inv.translator_disputed);
    } else if (approvalFilter === "disputed") {
      result = result.filter(inv => inv.translator_disputed);
    } else if (approvalFilter === "approved") {
      result = result.filter(inv => inv.admin_approved);
    }
    
    return result;
  };

  const filteredInvoices = getFilteredInvoices();

  // Count approvals
  const approvalCounts = invoices.reduce((acc, inv) => {
    if (inv.admin_approved) acc.approved++;
    else if (inv.translator_disputed) acc.disputed++;
    else if (inv.translator_confirmed) acc.pending_approval++;
    else acc.awaiting_confirmation++;
    return acc;
  }, { pending_approval: 0, awaiting_confirmation: 0, disputed: 0, approved: 0 });

  const handleExportExcel = async () => {
    setExporting({ ...exporting, excel: true });
    try {
      const params = new URLSearchParams();
      if (filters.taskType) params.append("task_type", filters.taskType);
      if (filters.resource) params.append("resource", filters.resource);
      if (filters.status) params.append("status", filters.status);

      const response = await axios.get(`${API}/export/excel?${params.toString()}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `graphikpro_tasks_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel file downloaded");
    } catch (error) {
      toast.error("Failed to export Excel");
    } finally {
      setExporting({ ...exporting, excel: false });
    }
  };

  const handleExportGrouped = async () => {
    setExporting({ ...exporting, grouped: true });
    try {
      const response = await axios.get(`${API}/export/grouped-worksheets`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `graphikpro_grouped_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Grouped worksheets downloaded");
    } catch (error) {
      toast.error("Failed to export grouped worksheets");
    } finally {
      setExporting({ ...exporting, grouped: false });
    }
  };

  const handleDownloadInvoicePDF = (invoice) => {
    generateInvoicePDF(invoice);
    toast.success("PDF downloaded");
  };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success("Invoice deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="export-center-page">
      <div className="page-header">
        <h1 className="page-title">Export Center</h1>
        <p className="page-subtitle">Download data and manage invoice history</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Filters */}
        <Card className="card lg:col-span-1">
          <CardHeader className="card-header">
            <CardTitle className="card-title">Export Filters</CardTitle>
          </CardHeader>
          <CardContent className="card-content space-y-4">
            <div className="space-y-2">
              <Label>Task Type</Label>
              <Select
                value={filters.taskType || "all"}
                onValueChange={(value) => setFilters({ ...filters, taskType: value === "all" ? "" : value })}
              >
                <SelectTrigger data-testid="filter-task-type">
                  <SelectValue placeholder="All tasks" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tasks</SelectItem>
                  {stats?.unique_tasks?.map((task, idx) => (
                    <SelectItem key={idx} value={task || `task-${idx}`}>{task}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Resource</Label>
              <Select
                value={filters.resource || "all"}
                onValueChange={(value) => setFilters({ ...filters, resource: value === "all" ? "" : value })}
              >
                <SelectTrigger data-testid="filter-resource">
                  <SelectValue placeholder="All resources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Resources</SelectItem>
                  {stats?.unique_resources?.map((resource, idx) => (
                    <SelectItem key={idx} value={resource || `resource-${idx}`}>{resource}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>{stats?.total_tasks || 0} total records</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>{stats?.completed_tasks || 0} completed</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Export Options */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="card card-hover">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Excel Export</h3>
                    <p className="text-sm text-slate-500 mb-3">Download all tasks with applied filters as Excel</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge className="badge badge-info">All Columns</Badge>
                      <Badge className="badge badge-info">Filtered Data</Badge>
                    </div>
                  </div>
                </div>
                <Button onClick={handleExportExcel} disabled={exporting.excel} className="btn-accent" data-testid="export-excel-btn">
                  {exporting.excel ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  {exporting.excel ? "Exporting..." : "Download"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="card card-hover">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Layers className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Grouped Worksheets</h3>
                    <p className="text-sm text-slate-500 mb-3">Tasks organized by Task and Resource in separate sheets</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge className="badge badge-success">By Task</Badge>
                      <Badge className="badge badge-success">By Resource</Badge>
                    </div>
                  </div>
                </div>
                <Button onClick={handleExportGrouped} disabled={exporting.grouped} className="btn-primary" data-testid="export-grouped-btn">
                  {exporting.grouped ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  {exporting.grouped ? "Exporting..." : "Download"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Summary */}
          <Card className="card">
            <CardHeader className="card-header">
              <CardTitle className="card-title flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-400" />
                Quick Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="card-content">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-slate-50">
                  <p className="text-2xl font-bold text-slate-900">{stats?.unique_tasks?.length || 0}</p>
                  <p className="text-xs text-slate-500">Task Types</p>
                </div>
                <div className="p-4 rounded-lg bg-slate-50">
                  <p className="text-2xl font-bold text-slate-900">{stats?.unique_resources?.length || 0}</p>
                  <p className="text-xs text-slate-500">Resources</p>
                </div>
                <div className="p-4 rounded-lg bg-emerald-50">
                  <p className="text-2xl font-bold text-emerald-700">{stats?.invoice_ready || 0}</p>
                  <p className="text-xs text-emerald-600">Invoice Ready</p>
                </div>
                <div className="p-4 rounded-lg bg-blue-50">
                  <p className="text-2xl font-bold text-blue-700">₹{(stats?.total_amount || 0).toLocaleString('en-IN')}</p>
                  <p className="text-xs text-blue-600">Total Amount</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Approval Summary */}
      {approvalCounts.pending_approval > 0 && (
        <Card className="card mt-6 border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Pending Admin Approval</p>
                  <p className="text-sm text-slate-500">{approvalCounts.pending_approval} invoice(s) confirmed by translators awaiting your approval</p>
                </div>
              </div>
              <Button
                onClick={() => setApprovalFilter("pending_approval")}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="view-pending-approvals-btn"
              >
                Review Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoice History with Status Tracking */}
      <Card className="card mt-6">
        <CardHeader className="card-header">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <CardTitle className="card-title flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-400" />
              Invoice History ({filteredInvoices.length})
            </CardTitle>
            
            <div className="flex flex-wrap items-center gap-2">
              {/* Approval Filter */}
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="approval-filter">
                <button
                  onClick={() => setApprovalFilter("all")}
                  className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                    approvalFilter === "all" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setApprovalFilter("pending_approval")}
                  className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                    approvalFilter === "pending_approval" ? "bg-blue-500 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="filter-pending-approval"
                >
                  Pending ({approvalCounts.pending_approval})
                </button>
                <button
                  onClick={() => setApprovalFilter("awaiting_confirmation")}
                  className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                    approvalFilter === "awaiting_confirmation" ? "bg-amber-500 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Awaiting ({approvalCounts.awaiting_confirmation})
                </button>
                <button
                  onClick={() => setApprovalFilter("approved")}
                  className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                    approvalFilter === "approved" ? "bg-emerald-500 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Approved ({approvalCounts.approved})
                </button>
                {approvalCounts.disputed > 0 && (
                  <button
                    onClick={() => setApprovalFilter("disputed")}
                    className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                      approvalFilter === "disputed" ? "bg-red-500 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    Disputed ({approvalCounts.disputed})
                  </button>
                )}
              </div>

              {/* Payment Status Filter */}
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="invoice-status-filter">
                <button
                  onClick={() => setStatusFilter("all")}
                  className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                    statusFilter === "all" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="filter-all"
                >
                  All
                </button>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                  const count = statusCounts[key] || 0;
                  if (count === 0 && key !== "paid" && key !== "unpaid") return null;
                  return (
                    <button
                      key={key}
                      onClick={() => setStatusFilter(key)}
                      className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                        statusFilter === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                      }`}
                      data-testid={`filter-${key}`}
                    >
                      {config.label}
                    </button>
                  );
                })}
              </div>
              
              <Button onClick={fetchData} variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid="refresh-invoices-btn">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredInvoices.length > 0 ? (
            <ScrollArea className="h-[450px]">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Approval</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => {
                    const approvalStatus = getApprovalStatus(invoice);
                    const approvalConfig = APPROVAL_STATUS_CONFIG[approvalStatus];
                    return (
                      <TableRow key={invoice.id} className="table-row-hover">
                        <TableCell className="font-mono text-xs">{invoice.invoice_number}</TableCell>
                        <TableCell className="text-sm">{invoice.invoice_date?.substring(0, 10)}</TableCell>
                        <TableCell>
                          <span className="font-medium text-sm">{invoice.resource_display_name || invoice.resource_name}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${approvalConfig.bg} ${approvalConfig.text} border ${approvalConfig.border} text-[10px]`}>
                            {approvalStatus === "approved" && <ShieldCheck className="w-3 h-3 mr-1" />}
                            {approvalStatus === "confirmed" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                            {approvalStatus === "pending" && <Clock className="w-3 h-3 mr-1" />}
                            {approvalStatus === "disputed" && <AlertCircle className="w-3 h-3 mr-1" />}
                            {approvalConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => openStatusDialog(invoice)}
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                            data-testid={`change-status-${invoice.id}`}
                          >
                            <StatusBadge status={invoice.status || "generated"} />
                          </button>
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold text-emerald-600">
                          ₹{invoice.grand_total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {/* Approve Button - only show if translator confirmed but admin hasn't approved */}
                            {invoice.translator_confirmed && !invoice.admin_approved && (
                              <Button
                                size="sm"
                                className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-[10px]"
                                onClick={() => handleApproveInvoice(invoice.id)}
                                disabled={approving === invoice.id}
                                data-testid={`approve-invoice-${invoice.id}`}
                              >
                                {approving === invoice.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>
                                    <ShieldCheck className="w-3 h-3 mr-1" />
                                    Approve
                                  </>
                                )}
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDownloadInvoicePDF(invoice)} data-testid={`download-invoice-${invoice.id}`}>
                              <Download className="w-3.5 h-3.5 text-blue-600" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDeleteInvoice(invoice.id)} data-testid={`delete-invoice-${invoice.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="font-medium">
                {statusFilter !== "all" ? `No ${statusFilter} invoices` : "No invoices generated yet"}
              </p>
              <p className="text-sm">
                {statusFilter !== "all"
                  ? "Try a different filter or generate new invoices"
                  : "Go to Invoice Generator or Resource Detail page to create invoices"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Update Dialog */}
      <Dialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <DialogContent className="sm:max-w-md" aria-describedby="status-dialog-desc">
          <DialogHeader>
            <DialogTitle>Update Invoice Status</DialogTitle>
            <p id="status-dialog-desc" className="text-sm text-slate-500">Change the payment status of this invoice</p>
          </DialogHeader>
          {statusDialog && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <p className="text-sm font-medium text-slate-900">{statusDialog.invoice_number}</p>
                <p className="text-xs text-slate-500">
                  {statusDialog.resource_display_name || statusDialog.resource_name} - ₹{statusDialog.grand_total?.toFixed(2)}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={statusForm.status} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${config.dot}`} />
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {statusForm.status === "paid" && (
                <div className="space-y-2">
                  <Label>Payment Date</Label>
                  <Input
                    type="date"
                    value={statusForm.payment_date}
                    onChange={(e) => setStatusForm({ ...statusForm, payment_date: e.target.value })}
                    data-testid="payment-date-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Add a note about this status change..."
                  value={statusForm.notes}
                  onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })}
                  rows={2}
                  data-testid="status-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)} data-testid="cancel-status-btn">
              Cancel
            </Button>
            <Button onClick={handleStatusUpdate} disabled={updatingStatus} data-testid="save-status-btn">
              {updatingStatus ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExportCenter;
